import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiUsers, 
  FiTarget, 
  FiEye, 
  FiHeart, 
  FiBookOpen, 
  FiStar, 
  FiAward, 
  FiGlobe, 
  FiMapPin, 
  FiCalendar
} = FiIcons;

const OrganizationOverview = () => {
  const [activeTab, setActiveTab] = useState('mission');

  // Core values displayed in multiple sections
  const coreValues = [
    {
      icon: FiHeart,
      title: 'Compassion',
      description: 'We approach our work with empathy and understanding, recognizing the unique challenges faced by young people in our communities.'
    },
    {
      icon: FiBookOpen,
      title: 'Education',
      description: 'We believe quality education is a right, not a privilege. We are committed to making learning accessible, engaging, and impactful.'
    },
    {
      icon: FiUsers,
      title: 'Community',
      description: 'We foster meaningful connections and collaborative learning environments where students support each other\'s growth.'
    },
    {
      icon: FiStar,
      title: 'Excellence',
      description: 'We strive for excellence in all aspects of our work, continuously improving our programs to deliver the best outcomes.'
    }
  ];

  // Team members for the About section
  const teamMembers = [
    {
      name: 'John Koroma',
      role: 'Founder & CEO',
      avatar: 'https://images.unsplash.com/photo-1531384441138-2736e62e0919?w=150&h=150&fit=crop&crop=face',
      bio: 'Passionate about education and youth development in Sierra Leone.'
    },
    {
      name: 'Aminata Sesay',
      role: 'Education Director',
      avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=150&h=150&fit=crop&crop=face',
      bio: 'Former teacher with 15 years of experience in curriculum development.'
    },
    {
      name: 'Ibrahim Jalloh',
      role: 'Community Manager',
      avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face',
      bio: 'Dedicated to building meaningful connections between students and mentors.'
    },
    {
      name: 'Fatmata Kamara',
      role: 'Program Coordinator',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face',
      bio: 'Coordinates our leadership programs across Sierra Leone and The Gambia.'
    }
  ];

  // Strategic goals for the Vision section
  const strategicGoals = [
    {
      year: '2025',
      goals: [
        'Reach 10,000 students across Sierra Leone and The Gambia',
        'Expand our educational content to cover all major subjects',
        'Establish 50 community learning hubs in underserved areas'
      ]
    },
    {
      year: '2027',
      goals: [
        'Extend our reach to 3 additional West African countries',
        'Launch a comprehensive teacher training program',
        'Develop advanced leadership certification programs'
      ]
    },
    {
      year: '2030',
      goals: [
        'Impact 100,000 young people across West Africa',
        'Create a self-sustaining educational ecosystem',
        'Establish ILM as the leading youth development organization in the region'
      ]
    }
  ];

  // Achievement stats for multiple sections
  const achievements = [
    { value: '5,000+', label: 'Students Impacted' },
    { value: '45+', label: 'Schools Partnered' },
    { value: '12', label: 'Countries Reached' },
    { value: '95%', label: 'Student Satisfaction' }
  ];

  // Events for the impact section
  const events = [
    {
      title: 'Leadership Summit 2024',
      date: 'May 15-17, 2024',
      location: 'Freetown, Sierra Leone',
      description: 'Annual gathering of young leaders from across West Africa'
    },
    {
      title: 'Teacher Training Workshop',
      date: 'June 10, 2024',
      location: 'Banjul, The Gambia',
      description: 'Equipping educators with innovative teaching methodologies'
    },
    {
      title: 'Digital Skills Bootcamp',
      date: 'July 5-25, 2024',
      location: 'Multiple Locations',
      description: 'Intensive training in essential technology skills'
    }
  ];

  return (
    <div className="space-y-8 pb-16 md:pb-0">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-8 text-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-white bg-opacity-10 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white bg-opacity-10 rounded-full -ml-24 -mb-24"></div>
        
        <div className="relative z-10 text-center">
          <div className="mx-auto w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mb-6">
            <span className="text-white font-bold text-2xl">ILM</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Inspirational Leadership Movement</h1>
          <p className="text-xl md:text-2xl text-primary-100 mb-6 max-w-2xl mx-auto">
            Empowering young people through education, mentorship, and leadership development
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Link to="/register" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors">
              Join Our Movement
            </Link>
            <a href="#learn-more" className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white hover:text-primary-600 transition-colors">
              Learn More
            </a>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {achievements.map((item, index) => (
              <div key={index} className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-2xl md:text-3xl font-bold">{item.value}</div>
                <div className="text-sm md:text-base">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Navigation Tabs */}
      <div id="learn-more" className="bg-white rounded-xl shadow-lg p-4 md:p-6 border border-gray-100 overflow-x-auto">
        <div className="flex space-x-2 md:space-x-4 min-w-max md:min-w-0">
          {[
            { id: 'mission', label: 'Our Mission', icon: FiTarget },
            { id: 'vision', label: 'Our Vision', icon: FiEye },
            { id: 'values', label: 'Core Values', icon: FiHeart },
            { id: 'about', label: 'About Us', icon: FiUsers },
            { id: 'impact', label: 'Our Impact', icon: FiAward }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-1 md:space-x-2 px-3 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-all whitespace-nowrap text-sm md:text-base ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4 md:w-5 md:h-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Mission Section */}
      {activeTab === 'mission' && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <div className="text-center">
              <SafeIcon icon={FiTarget} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Mission</h2>
              <div className="max-w-3xl mx-auto">
                <p className="text-lg md:text-xl text-gray-700 italic font-medium mb-6">
                  "To empower young people in Sierra Leone and The Gambia through inspirational education, 
                  mentorship, and leadership development, creating a generation of confident, skilled leaders 
                  who will transform their communities."
                </p>
                <p className="text-gray-600 text-sm md:text-base">
                  At ILM Global, we are dedicated to breaking down barriers to education and providing 
                  young people with the tools, knowledge, and support they need to thrive in an increasingly 
                  complex world. We believe that education is the foundation upon which sustainable 
                  development and lasting change are built.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
            >
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <SafeIcon icon={FiTarget} className="w-6 h-6 mr-2 text-primary-600" />
                What We Do
              </h3>
              <div className="space-y-4 text-gray-600 text-sm md:text-base">
                <p>
                  Through our educational platform, we provide access to high-quality learning materials, 
                  interactive quizzes, and study resources tailored to local curricula. Our focus areas include:
                </p>
                <ul className="space-y-2 list-disc pl-5">
                  <li><strong>Academic Excellence:</strong> Supporting core subjects with comprehensive study materials</li>
                  <li><strong>Digital Literacy:</strong> Equipping students with essential technology skills</li>
                  <li><strong>Leadership Development:</strong> Fostering the next generation of community leaders</li>
                  <li><strong>Peer Learning:</strong> Creating collaborative spaces for knowledge sharing</li>
                  <li><strong>Mentorship:</strong> Connecting students with role models and mentors</li>
                </ul>
                <p>
                  We work closely with schools, communities, and government agencies to ensure our programs 
                  address real needs and create meaningful impact.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
            >
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <SafeIcon icon={FiBookOpen} className="w-6 h-6 mr-2 text-primary-600" />
                How We Work
              </h3>
              <div className="space-y-4 text-gray-600 text-sm md:text-base">
                <p>
                  Our approach is built on these key principles:
                </p>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-1">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">Accessibility</h4>
                      <p>Making education available to all, regardless of location or economic background</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-1">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">Localization</h4>
                      <p>Tailoring content to reflect local context, cultures, and educational needs</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-1">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">Collaboration</h4>
                      <p>Working with local partners to enhance impact and sustainability</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-1">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">Innovation</h4>
                      <p>Continuously improving our methods and embracing technology to enhance learning</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}

      {/* Vision Section */}
      {activeTab === 'vision' && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <div className="text-center">
              <SafeIcon icon={FiEye} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Vision</h2>
              <div className="max-w-3xl mx-auto">
                <p className="text-lg md:text-xl text-gray-700 italic font-medium mb-6">
                  "We envision a West Africa where every young person is empowered through education 
                  to reach their full potential and become a catalyst for positive change in their communities."
                </p>
                <p className="text-gray-600 text-sm md:text-base">
                  At ILM Global, we look toward a future where education transcends traditional boundaries, 
                  where leadership is cultivated from an early age, and where young people are equipped 
                  with the knowledge, skills, and confidence to address the unique challenges facing 
                  their communities and the broader world.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center">
              <SafeIcon icon={FiTarget} className="w-6 h-6 mr-2 text-primary-600" />
              Strategic Goals
            </h3>
            <div className="space-y-8">
              {strategicGoals.map((period, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="flex flex-col md:flex-row gap-6"
                >
                  <div className="md:w-1/4">
                    <div className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-lg p-4 text-white text-center">
                      <SafeIcon icon={FiCalendar} className="w-8 h-8 mx-auto mb-2" />
                      <h4 className="text-xl font-bold">{period.year}</h4>
                      <p className="text-sm opacity-80">Strategic Milestone</p>
                    </div>
                  </div>
                  <div className="md:w-3/4 bg-gray-50 rounded-lg p-5">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">By {period.year}, we aim to:</h4>
                    <ul className="space-y-2">
                      {period.goals.map((goal, goalIndex) => (
                        <li key={goalIndex} className="flex items-start">
                          <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                            <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                          </div>
                          <span className="text-gray-700">{goal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-6 md:p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-6 text-center">The Future We See</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div className="bg-white bg-opacity-10 rounded-lg p-5">
                <h4 className="text-lg font-semibold mb-3">Communities Transformed</h4>
                <p className="text-primary-100 text-sm">
                  We see communities across West Africa transformed by a new generation of leaders who 
                  are equipped with the knowledge, skills, and values to address local challenges and 
                  drive sustainable development.
                </p>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-5">
                <h4 className="text-lg font-semibold mb-3">Education Reimagined</h4>
                <p className="text-primary-100 text-sm">
                  We envision an educational approach that transcends traditional models, embracing 
                  technology and innovation to create engaging, accessible learning experiences that 
                  prepare young people for the challenges of the 21st century.
                </p>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-5">
                <h4 className="text-lg font-semibold mb-3">Leadership Cultivated</h4>
                <p className="text-primary-100 text-sm">
                  We see a West Africa where leadership is nurtured from an early age, where young 
                  people are empowered to lead with integrity, compassion, and a deep commitment 
                  to the common good.
                </p>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-5">
                <h4 className="text-lg font-semibold mb-3">Opportunity Created</h4>
                <p className="text-primary-100 text-sm">
                  We envision a future where young people have the tools and confidence to create 
                  economic opportunities for themselves and their communities, breaking cycles of 
                  poverty and dependency.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* Values Section */}
      {activeTab === 'values' && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <div className="text-center">
              <SafeIcon icon={FiHeart} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Core Values</h2>
              <p className="text-gray-600 max-w-3xl mx-auto mb-8 text-sm md:text-base">
                Our values guide everything we do, from program development to community engagement. 
                They reflect our commitment to creating a positive, transformative impact in the lives 
                of young people across West Africa.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {coreValues.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className="bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow"
                >
                  <div className="bg-primary-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={value.icon} className="w-7 h-7 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 text-center mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm text-center">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">How Our Values Guide Us</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-primary-600 mb-3">In Our Programs</h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <span>Creating inclusive learning environments that welcome all students</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <span>Designing curriculum that respects local cultures and contexts</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <span>Setting high standards while providing necessary support</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <span>Emphasizing collaboration and peer learning</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-primary-600 mb-3">In Our Partnerships</h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <span>Working collaboratively with local schools and community organizations</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <span>Maintaining transparency and accountability in all relationships</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <span>Listening to and incorporating community feedback</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                    <span>Sharing knowledge and resources to maximize collective impact</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* About Us Section */}
      {activeTab === 'about' && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <div className="text-center">
              <SafeIcon icon={FiUsers} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-800 mb-4">About ILM Global</h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-sm md:text-base">
                Inspirational Leadership Movement (ILM) Global is a non-profit organization dedicated 
                to empowering young people through education, mentorship, and leadership development 
                in Sierra Leone and The Gambia. Founded in 2015, we've been on a mission to transform 
                lives and communities through accessible quality education.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiBookOpen} className="w-6 h-6 mr-2 text-primary-600" />
                  Our Story
                </h3>
                <p className="text-gray-600 mb-4 text-sm md:text-base">
                  ILM Global began as a small initiative in Freetown, Sierra Leone, with just 25 students 
                  and 3 volunteer mentors. Our founder, John Koroma, witnessed firsthand the challenges 
                  young people faced in accessing quality education and mentorship.
                </p>
                <p className="text-gray-600 text-sm md:text-base">
                  What started as weekend study sessions in a small classroom has grown into a comprehensive 
                  platform reaching thousands of students across West Africa. Our journey has been one of 
                  persistence, passion, and the unwavering belief that every young person deserves the 
                  opportunity to learn and lead.
                </p>
              </div>
              <div className="flex-1">
                <img
                  src="https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=800"
                  alt="Students studying together"
                  className="rounded-lg shadow-md w-full h-auto object-cover"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center">
              <SafeIcon icon={FiHeart} className="w-6 h-6 mr-2 text-primary-600" />
              Meet Our Team
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="bg-gray-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow"
                >
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-24 h-24 rounded-full mx-auto mb-3 object-cover border-4 border-white shadow-sm"
                  />
                  <h4 className="text-lg font-semibold text-gray-800">{member.name}</h4>
                  <p className="text-primary-600 text-sm mb-2">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gray-50 rounded-xl shadow-md p-6 md:p-8 border border-gray-100"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Get In Touch</h3>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1 space-y-4">
                <p className="text-gray-600 text-sm md:text-base">
                  We'd love to hear from you! Whether you're interested in our programs, partnership 
                  opportunities, or just want to learn more about our work, please reach out.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <SafeIcon icon={FiGlobe} className="w-5 h-5 text-primary-600 mr-2" />
                    <span className="text-gray-700">www.ilmglobal.org</span>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiUsers} className="w-5 h-5 text-primary-600 mr-2" />
                    <span className="text-gray-700">info@ilmglobal.org</span>
                  </div>
                  <div className="flex items-start">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 text-primary-600 mr-2 mt-1" />
                    <span className="text-gray-700">
                      Main Office: 15 Wilkinson Road, Freetown, Sierra Leone<br />
                      Gambia Office: 7 Independence Drive, Banjul, The Gambia
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                      <input
                        type="text"
                        id="name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Enter your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Enter your email"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                      <textarea
                        id="message"
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="How can we help you?"
                      ></textarea>
                    </div>
                    <button className="w-full bg-primary-500 text-white font-medium py-2 px-4 rounded-md hover:bg-primary-600 transition-colors">
                      Send Message
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* Impact Section */}
      {activeTab === 'impact' && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <div className="text-center">
              <SafeIcon icon={FiAward} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Impact</h2>
              <p className="text-gray-600 max-w-3xl mx-auto mb-8 text-sm md:text-base">
                Since 2015, ILM Global has been making a significant difference in the lives of young people 
                across West Africa. Here's a glimpse of what we've achieved together with our partners, 
                supporters, and the committed young leaders we serve.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
                {achievements.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 + index * 0.1 }}
                    className="bg-gray-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow"
                  >
                    <div className="text-2xl md:text-3xl font-bold text-primary-600">{item.value}</div>
                    <div className="text-sm md:text-base text-gray-600">{item.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Success Stories</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <img
                    src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=100&h=100&fit=crop&crop=face"
                    alt="Student portrait"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-800">Mariama B.</h4>
                    <p className="text-sm text-gray-600">Freetown, Sierra Leone</p>
                  </div>
                </div>
                <p className="text-gray-700 italic mb-3">
                  "Before joining ILM Global, I struggled with mathematics and lacked confidence in my abilities. 
                  The mentorship and study resources transformed my approach to learning. Today, I'm studying 
                  engineering and mentoring other young women in STEM fields."
                </p>
                <p className="text-sm text-primary-600 font-medium">Now studying Engineering at Fourah Bay College</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <img
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
                    alt="Student portrait"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-800">Ibrahim J.</h4>
                    <p className="text-sm text-gray-600">Banjul, The Gambia</p>
                  </div>
                </div>
                <p className="text-gray-700 italic mb-3">
                  "The leadership training I received through ILM Global gave me the skills and confidence to 
                  start a community recycling initiative in my neighborhood. What began with just 5 volunteers 
                  has grown to impact over 1,000 community members."
                </p>
                <p className="text-sm text-primary-600 font-medium">Founder of EcoYouth Gambia</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Upcoming Events</h3>
            <div className="space-y-4">
              {events.map((event, index) => (
                <div key={index} className="flex flex-col md:flex-row gap-4 bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <div className="md:w-1/4 flex flex-col justify-center items-center bg-primary-100 rounded-lg p-4 text-center">
                    <SafeIcon icon={FiCalendar} className="w-8 h-8 text-primary-600 mb-2" />
                    <p className="font-bold text-gray-800">{event.date}</p>
                  </div>
                  <div className="md:w-3/4">
                    <h4 className="text-lg font-semibold text-gray-800">{event.title}</h4>
                    <div className="flex items-center text-gray-600 text-sm mb-2">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-1" />
                      <span>{event.location}</span>
                    </div>
                    <p className="text-gray-700 mb-3">{event.description}</p>
                    <button className="bg-primary-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors">
                      Learn More
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-6 md:p-8 text-white text-center"
      >
        <h2 className="text-2xl font-bold mb-4">Join Our Movement</h2>
        <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
          Together, we can create a brighter future for young people in West Africa. 
          Whether you are a student, educator, volunteer, or donor, there is a place for you in our movement.
        </p>
        <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
          <Link to="/register" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors">
            Join ILM Global
          </Link>
          <button className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white hover:text-primary-600 transition-colors">
            Support Our Work
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default OrganizationOverview;